import { ChangeEvent, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
	Box,
	Checkbox,
	Chip,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
// import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined"

import { priceToString } from "../../../shared/utils/format-money";
import {
	formatRelativeDay,
	formatTime,
	isDateInRange,
} from "../../../shared/utils/format-date";
import {
	formatDocumentLabel,
	formatDocumentParts,
} from "../../../shared/utils/format-document";
import { Paginator } from "../../../shared/components/paginator";
import { OrdersSearchFilter } from "./OrdersSearchFilter";
import { OrdersPeriodFilter } from "./OrdersPeriodFilter";
import { OrdersStatusFilter } from "./OrdersStatusFilter";
import { OrdersExportButton } from "./OrdersExportButton";
import { getPeriodRange } from "./periodPresets";
import { OrderStatus, statusColor } from "./orderStatus";
import { OrderPayment, paymentLabel } from "./orderTypes";
import { mockOrders } from "../mockOrders";
import { TableRowColors } from "../../../shared/styles/theme";

const PAGE_SIZE = 20;

const pixIcon = require("../../../assets/payment-icons/pix.svg").default;
const mastercardIcon =
	require("../../../assets/payment-icons/mastercard.svg").default;

const CHECKBOX_COLUMN_WIDTH = 52;
const ORDER_COLUMN_WIDTH = 100;
const DATE_COLUMN_LEFT = CHECKBOX_COLUMN_WIDTH + ORDER_COLUMN_WIDTH;

const STICKY_SEAM_OVERLAP = 1;

const fixedStickyColumnWidth = (width: number) => ({
	width: width + STICKY_SEAM_OVERLAP,
	minWidth: width + STICKY_SEAM_OVERLAP,
	boxSizing: "border-box" as const,
});

const stickyHeaderCell = (left: number) => ({
	position: "sticky" as const,
	left,
	zIndex: 3,
});

const stickyBodyCell = (left: number) => ({
	position: "sticky" as const,
	left,
	zIndex: 1,
	backgroundColor: "#ffffff",
	".MuiTableRow-hover:hover > &": {
		backgroundImage: `linear-gradient(${TableRowColors.hover}, ${TableRowColors.hover})`,
	},
	".Mui-selected > &": {
		backgroundImage: `linear-gradient(${TableRowColors.selected}, ${TableRowColors.selected})`,
	},
	".Mui-selected:hover > &": {
		backgroundImage: `linear-gradient(${TableRowColors.selectedHover}, ${TableRowColors.selectedHover})`,
	},
});

const lastStickyColumnDivider = {
	boxShadow: "inset -1px 0 0 rgba(0, 0, 0, 0.12)",
};

const paymentIcon: Record<OrderPayment, JSX.Element> = {
	pix: <img src={pixIcon} alt="PIX" width={32} height={32} />,
	credit_card: (
		<img src={mastercardIcon} alt="Mastercard" width={32} height={32} />
	),
};

export function OrdersList() {
	const [selectedIds, setSelectedIds] = useState<string[]>([]);

	const [searchParams, setSearchParams] = useSearchParams();
	const search = searchParams.get("search") ?? "";
	const status = searchParams.get("status") as OrderStatus | null;
	const [periodFrom, periodTo] = getPeriodRange(searchParams);
	const page = Number(searchParams.get("page") ?? "1");

	const filteredOrders = mockOrders.filter((order) => {
		if (!isDateInRange(order.date, periodFrom, periodTo)) return false;
		if (status && order.status !== status) return false;

		const term = search.trim().toLowerCase();
		if (!term) return true;

		const digitsTerm = term.replace(/\D/g, "");

		return (
			order.orderNumber.toLowerCase().includes(term) ||
			order.client.toLowerCase().includes(term) ||
			order.email.toLowerCase().includes(term) ||
			order.plate.toLowerCase().includes(term) ||
			formatDocumentLabel(order.document).toLowerCase().includes(term) ||
			(digitsTerm.length > 0 && order.document.includes(digitsTerm))
		);
	});

	const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
	const currentPage = Math.min(page, totalPages);
	const pagedOrders = filteredOrders.slice(
		(currentPage - 1) * PAGE_SIZE,
		currentPage * PAGE_SIZE,
	);

	function handleChangePage(_: ChangeEvent<unknown>, newPage: number) {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.set("page", String(newPage));
				return next;
			},
			{ replace: true },
		);
	}

	const allSelected =
		filteredOrders.length > 0 &&
		filteredOrders.every((order) => selectedIds.includes(order.id));
	const someSelected =
		filteredOrders.some((order) => selectedIds.includes(order.id)) &&
		!allSelected;

	function toggleAll() {
		const filteredIds = filteredOrders.map((order) => order.id);
		setSelectedIds((prev) =>
			allSelected
				? prev.filter((id) => !filteredIds.includes(id))
				: Array.from(new Set([...prev, ...filteredIds])),
		);
	}

	function toggleOne(id: string) {
		setSelectedIds((prev) =>
			prev.includes(id)
				? prev.filter((selectedId) => selectedId !== id)
				: [...prev, id],
		);
	}

	const selectedOrders = mockOrders.filter((order) =>
		selectedIds.includes(order.id),
	);

	return (
		<Box>
			<Box
				sx={{
					mb: 3,
					display: "flex",
					flexDirection: { xs: "column", sm: "row" },
					flexWrap: "wrap",
					gap: 2,
					alignItems: { xs: "stretch", sm: "center" },
					justifyContent: "space-between",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexWrap: "wrap",
						gap: 2,
						alignItems: "center",
					}}
				>
					<OrdersSearchFilter />
					<OrdersPeriodFilter />
					<OrdersStatusFilter />
				</Box>

				<OrdersExportButton orders={selectedOrders} />
			</Box>

			{filteredOrders.length === 0 ? (
				<Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
					Nenhum pedido encontrado para os filtros aplicados.
				</Typography>
			) : (
				<TableContainer
					component={Paper}
					variant="outlined"
					sx={{ overflowX: "auto" }}
				>
					<Table
						size="small"
						sx={{ minWidth: 900, width: "100%", tableLayout: "auto" }}
					>
						<TableHead>
							<TableRow>
								<TableCell
									padding="checkbox"
									sx={{
										...stickyHeaderCell(0),
										color: "#ffffff",
										...fixedStickyColumnWidth(CHECKBOX_COLUMN_WIDTH),
									}}
								>
									<Checkbox
										checked={allSelected}
										indeterminate={someSelected}
										onChange={toggleAll}
										sx={{
											color: "#ffffff",
											"&.Mui-checked": { color: "#ffffff" },
											"&.MuiCheckbox-indeterminate": { color: "#ffffff" },
										}}
									/>
								</TableCell>
								<TableCell
									align="center"
									sx={{
										...stickyHeaderCell(CHECKBOX_COLUMN_WIDTH),
										color: "#ffffff",
										...fixedStickyColumnWidth(ORDER_COLUMN_WIDTH),
									}}
								>
									<strong>Pedido</strong>
								</TableCell>
								<TableCell
									sx={{
										...stickyHeaderCell(DATE_COLUMN_LEFT),
										...lastStickyColumnDivider,
										color: "#ffffff",
									}}
								>
									<strong>Data</strong>
								</TableCell>
								<TableCell sx={{ color: "#ffffff" }}>
									<strong>Cliente</strong>
								</TableCell>
								<TableCell align="center" sx={{ color: "#ffffff" }}>
									<strong>Situação</strong>
								</TableCell>
								<TableCell sx={{ color: "#ffffff" }}>
									<strong>Tipo de Consulta</strong>
								</TableCell>
								<TableCell sx={{ color: "#ffffff" }}>
									<strong>Placa</strong>
								</TableCell>
								<TableCell sx={{ color: "#ffffff" }}>
									<strong>Pagamento</strong>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{pagedOrders.map((order) => {
								const isSelected = selectedIds.includes(order.id);
								return (
									<TableRow key={order.id} selected={isSelected} hover>
										<TableCell
											padding="checkbox"
											sx={{
												...stickyBodyCell(0),
												...fixedStickyColumnWidth(CHECKBOX_COLUMN_WIDTH),
											}}
										>
											<Checkbox
												checked={isSelected}
												onChange={() => toggleOne(order.id)}
											/>
										</TableCell>
										<TableCell
											align="center"
											sx={{
												...stickyBodyCell(CHECKBOX_COLUMN_WIDTH),
												color: "primary.main",
												fontWeight: "bold",
												...fixedStickyColumnWidth(ORDER_COLUMN_WIDTH),
											}}
										>
											<Link
												to={`/pedido/${order.id}`}
												state={{ from: `/pedidos?${searchParams.toString()}` }}
												title="Ver detalhes"
												style={{ color: "inherit", textDecoration: "none" }}
											>
												{order.orderNumber}
											</Link>
										</TableCell>
										<TableCell
											sx={{
												...stickyBodyCell(DATE_COLUMN_LEFT),
												...lastStickyColumnDivider,
											}}
										>
											<Typography variant="body2" sx={{ display: "block" }}>
												{formatRelativeDay(order.date)}
											</Typography>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{ display: "block" }}
											>
												{formatTime(order.date)}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" sx={{ display: "block" }}>
												{order.client}
											</Typography>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 0.5,
													color: "text.secondary",
												}}
											>
												{/* <BadgeOutlinedIcon sx={{ fontSize: 14 }} /> */}
												<Typography variant="caption" color="text.secondary">
													{formatDocumentParts(order.document).value}
												</Typography>
											</Box>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{ display: "block" }}
											>
												{order.email}
											</Typography>
										</TableCell>
										<TableCell align="center">
											<Chip
												size="small"
												label={order.status}
												color={statusColor[order.status]}
												variant="filled"
												sx={{ width: "100%" }}
											/>
										</TableCell>
										<TableCell>{order.queryType}</TableCell>
										<TableCell>
											<Box
												sx={{
													color: "primary.main",
													fontWeight: 600,
													letterSpacing: 1.5,
												}}
											>
												{order.plate}
											</Box>
										</TableCell>
										<TableCell>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 0.25,
												}}
											>
												<Typography
													variant="body2"
													sx={{ fontWeight: 500, whiteSpace: "nowrap" }}
												>
													{`R$ ${priceToString(order.value)}`}
												</Typography>
												<Tooltip title={paymentLabel[order.payment]}>
													<Box
														component="span"
														sx={{
															display: "inline-flex",
															width: 32,
															justifyContent: "center",
														}}
													>
														{paymentIcon[order.payment]}
													</Box>
												</Tooltip>
											</Box>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			{filteredOrders.length > 0 && (
				<Paginator
					count={filteredOrders.length}
					pageNumber={currentPage}
					qtdResults={PAGE_SIZE}
					onChange={handleChangePage}
				/>
			)}
		</Box>
	);
}
